import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { cardImageCache } from "@/services/card-image-cache";
import { useEffect, useCallback } from "react";
import { getSixToKingDeck } from "@shared/game-rules";
import { Capacitor } from "@capacitor/core";
import { useLocalDecks } from "./use-local-decks";
import { USE_LOCAL_ASSETS } from "@/config/constants";

export function useDefaultDeck() {
  const { token } = useAuth();
  const { getDeckFromUrl } = useLocalDecks();
  
  const query = useQuery({
    queryKey: ["default-deck"],
    queryFn: async () => {
      let apiUrl = "/api/deck/default-deck";
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }
      
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        cache: "no-store", // Prevent caching issues
      });
      
      if (!response.ok) throw new Error("Failed to fetch default deck");
      const data = await response.json();
      
      // If using local assets, replace card URLs with local paths
      if (USE_LOCAL_ASSETS && data?.deckUrl) {
        const localCards = getDeckFromUrl(data.deckUrl);
        
        return {
          ...data,
          cards: localCards,
          // Keep original deck metadata from API
          name: data.name,
          description: data.description,
          id: data.id,
          deckUrl: data.deckUrl, // Keep original for reference
          // Mark as using local assets
          isLocal: true
        };
      }
      
      return data;
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Preload all deck images when the deck data is available (only if not using local assets)
  useEffect(() => {
    if (!USE_LOCAL_ASSETS && query.data?.deckUrl) {
      // Get the standard deck of cards
      const cardDeck = getSixToKingDeck();
      
      // Preload all card images for this deck
      cardImageCache.preloadDeckImages(query.data.deckUrl, cardDeck);
    }
  }, [query.data?.deckUrl]);
  
  return query;
}
